(function () {
	let dynamicContrastController = null;

	const getLandingPageContext = () => {
		const body = document.body;
		if (!body) {
			return {
				isLanding: false,
				hasTemplateClass: false,
				hasTemplateToken: false,
				bodyClasses: [],
				templateTokens: [],
			};
		}

		const bodyClasses = Array.from(body.classList ?? []);
		const hasTemplateClass = bodyClasses.includes("template-index");

		const templateTokens = [
			body.dataset?.template,
			body.dataset?.templateName,
			body.getAttribute?.("data-template"),
		].filter(Boolean);

		const hasTemplateToken = templateTokens.some((token) =>
			token?.toLowerCase?.()?.includes?.("index")
		);

		return {
			isLanding: hasTemplateClass || hasTemplateToken,
			hasTemplateClass,
			hasTemplateToken,
			bodyClasses,
			templateTokens,
		};
	};

	const resetHeaderContrast = (target) => {
		if (!target) return;
		target.classList.remove(
			"header--contrast-ready",
			"header--contrast-light",
			"header--contrast-dark",
			"header--contrast-has-overlay"
		);
		if (target.dataset) {
			delete target.dataset.headerTone;
		}
	};

	const parseRGB = (input) => {
		if (!input || input === "transparent") return null;

		const match = input.match(/rgba?\(([^)]+)\)/);
		if (!match) return null;

		const parts = match[1]
			.split(",")
			.map((value) => Number.parseFloat(value.trim()));

		if (parts.length < 3) return null;

		let [r, g, b, a] = parts;
		if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
			return null;
		}

		a = Number.isNaN(a) ? 1 : a;

		return { r, g, b, a };
	};

	const relativeLuminance = ({ r, g, b, a }) => {
		if (a === 0) {
			return 1;
		}

		const transform = (channel) => {
			const value = channel / 255;
			return value <= 0.03928
				? value / 12.92
				: Math.pow((value + 0.055) / 1.055, 2.4);
		};

		const sr = transform(r);
		const sg = transform(g);
		const sb = transform(b);

		return 0.2126 * sr + 0.7152 * sg + 0.0722 * sb;
	};

	const normalizeSchemeValue = (value) => value?.toLowerCase?.() ?? "";

	const resolveToneFromNode = (node) => {
		if (!node) return null;

		const dataTone = node.dataset?.headerTone;
		if (dataTone === "dark" || dataTone === "light") {
			return { tone: dataTone, source: "dataset:headerTone", node };
		}

		const schemeData =
			node.dataset?.scheme ||
			node.getAttribute?.("data-scheme") ||
			node.dataset?.colorScheme ||
			node.getAttribute?.("data-color-scheme");
		const normalizedScheme = normalizeSchemeValue(schemeData);
		if (normalizedScheme) {
			if (
				normalizedScheme.includes("dark") ||
				normalizedScheme.includes("inverse")
			) {
				return { tone: "dark", source: `data-scheme:${normalizedScheme}`, node };
			}
			if (normalizedScheme.includes("light")) {
				return { tone: "light", source: `data-scheme:${normalizedScheme}`, node };
			}
		}

		const classList = node.classList;
		if (classList) {
			const darkHints = [
				"section--has-overlay",
				"section--have-overlay",
				"section--with-overlay",
				"color-background-2",
				"color-inverse",
				"background-secondary",
				"bg--dark",
				"overlay",
			];
			const lightHints = [
				"color-background-1",
				"background-default",
				"background-primary",
				"bg--light",
			];

			if (darkHints.some((hint) => classList.contains(hint))) {
				return { tone: "dark", source: "class:hints-dark", node };
			}
			if (lightHints.some((hint) => classList.contains(hint))) {
				return { tone: "light", source: "class:hints-light", node };
			}
		}

		const style = window.getComputedStyle(node);
		if (style) {
			const background = style.backgroundColor;
			const hasGradient = background && background.includes("gradient");

			if (
				background &&
				background !== "transparent" &&
				background !== "rgba(0, 0, 0, 0)" &&
				!hasGradient
			) {
				const parsed = parseRGB(background);
				if (parsed) {
					const luminance = relativeLuminance(parsed);
					return {
						tone: luminance <= 0.5 ? "dark" : "light",
						source: "computed-color",
						luminance,
						node,
					};
				}
			}
		}

		return null;
	};

	const detectTone = (startElement) => {
		let element = startElement;
		const root = document.documentElement;

		while (element && element !== root) {
			const resolved = resolveToneFromNode(element);
			if (resolved) {
				return resolved;
			}
			element = element.parentElement;
		}

		return {
			tone: "light",
			source: "fallback",
			node: root,
			luminance: 1,
		};
	};

	const createDynamicHeaderContrast = (initialHeader) => {
		const state = {
			header: initialHeader,
			framePending: false,
			currentTone: "",
		};

		const cleanupFns = [];

		const addListener = (target, event, handler, options) => {
			target.addEventListener(event, handler, options);
			cleanupFns.push(() =>
				target.removeEventListener(event, handler, options?.capture ?? false)
			);
		};

		const getMeasurableTarget = (element) => {
			if (!element) return { target: null, rect: null, fallbackUsed: false };

			const directRect = element.getBoundingClientRect();
			if (directRect.width > 0 && directRect.height > 0) {
				return { target: element, rect: directRect, fallbackUsed: false };
			}

			const candidates = [
				element.querySelector(".header-wrapper"),
				element.querySelector(".header"),
				element.querySelector(".header__container"),
				element.firstElementChild,
			].filter(Boolean);

			for (const candidate of candidates) {
				const rect = candidate.getBoundingClientRect();
				if (rect.width > 0 && rect.height > 0) {
					return { target: candidate, rect, fallbackUsed: true };
				}
			}

			return { target: element, rect: directRect, fallbackUsed: false };
		};

		const updateTone = () => {
			const header = state.header;
			if (!header || !header.isConnected) {
				console.log("[HeaderContrast]", "updateTone skipped", {
					reason: "header missing or detached",
				});
				return;
			}

			const { target: measurementTarget, rect, fallbackUsed } =
				getMeasurableTarget(header);

			if (!rect || rect.width === 0 || rect.height === 0) {
				console.log("[HeaderContrast]", "updateTone skipped", {
					reason: "header has no size",
					rect,
				});
				return;
			}

			const sampleX = Math.min(
				Math.max(rect.left + rect.width / 2, 0),
				window.innerWidth - 1
			);
			const sampleY = Math.min(
				Math.max(rect.top + rect.height / 2, 0),
				window.innerHeight - 1
			);

			console.log("[HeaderContrast]", "updateTone sampling", {
				rect,
				sampleX,
				sampleY,
				fallbackUsed,
				measurementTarget:
					measurementTarget?.className ??
					measurementTarget?.tagName ??
					"unknown",
			});

			const targetForPointerToggle = measurementTarget || header;

			const previousPointerEvents = targetForPointerToggle.style.pointerEvents;
			targetForPointerToggle.style.pointerEvents = "none";
			const elementsAtPoint = document.elementsFromPoint(sampleX, sampleY);
			targetForPointerToggle.style.pointerEvents = previousPointerEvents;

			const candidateElements = elementsAtPoint.filter((el) => {
				if (!el) return false;
				if (header.contains(el)) return false;
				if (el === document.documentElement) return true;
				if (el === document.body) return true;
				return true;
			});
			const sampledElement = candidateElements[0] || elementsAtPoint[0];

			if (!sampledElement) {
				console.log("[HeaderContrast]", "updateTone skipped", {
					reason: "no sampled element",
					sampleX,
				sampleY,
				candidates: candidateElements
					.slice(0, 5)
					.map(
						(el) =>
							el?.className ??
							el?.tagName ??
							"unknown"
					),
			});
				return;
			}

			let toneResult = detectTone(sampledElement);

			if (
				toneResult?.tone === "light" &&
				candidateElements.length > 1
			) {
				for (let index = 1; index < candidateElements.length; index += 1) {
					const candidate = candidateElements[index];
					const alternative = detectTone(candidate);
					if (alternative && alternative.tone !== toneResult.tone) {
						console.log("[HeaderContrast]", "updateTone alternative candidate", {
							previousTone: toneResult.tone,
							newTone: alternative.tone,
							candidate:
								candidate?.className ?? candidate?.tagName ?? "unknown",
							source: alternative.source,
						});
						toneResult = alternative;
						break;
					}
				}
			}

			const tone = toneResult.tone;

			console.log("[HeaderContrast]", "updateTone resolved tone", {
				tone,
				source: toneResult.source,
				luminance: toneResult.luminance ?? null,
				sampledElement:
					sampledElement?.className ?? sampledElement?.tagName ?? "unknown",
				inferredFrom:
					toneResult.node?.className ?? toneResult.node?.tagName ?? "unknown",
			});

			const baseLogo = header.querySelector(
				".header__heading-logo:not(.header__heading-logo--overlay)"
			);
			const overlayLogo = header.querySelector(".header__heading-logo--overlay");

			if (baseLogo && overlayLogo) {
				header.classList.add("header--contrast-has-overlay");
			} else {
				header.classList.remove("header--contrast-has-overlay");
			}

			if (
				tone === state.currentTone &&
				header.classList.contains("header--contrast-ready")
			) {
				console.log("[HeaderContrast]", "Tone unchanged", {
					reason: "same tone",
					tone,
					source: toneResult.source,
					sampledElement:
						sampledElement?.className ?? sampledElement?.tagName ?? "unknown",
					inferredFrom:
						toneResult.node?.className ?? toneResult.node?.tagName ?? "unknown",
				});
				return;
			}

			state.currentTone = tone;
			header.classList.add("header--contrast-ready");
			header.classList.toggle("header--contrast-light", tone === "dark");
			header.classList.toggle("header--contrast-dark", tone === "light");
			header.dataset.headerTone = tone;

			console.log("[HeaderContrast]", "Tone update", {
				tone,
				source: toneResult.source,
				luminance: toneResult.luminance ?? null,
				sampledElement:
					sampledElement?.className ?? sampledElement?.tagName ?? "unknown",
				inferredFrom: toneResult.node?.className ?? toneResult.node?.tagName ?? "unknown",
			});
		};

		const scheduleUpdate = () => {
			if (state.framePending) {
				return false;
			}
			state.framePending = true;
			requestAnimationFrame(() => {
				state.framePending = false;
				updateTone();
			});
			return true;
		};

		const scheduleUpdateWithLog = (eventName) => {
			if (state.framePending) {
				console.log("[HeaderContrast]", "scheduleUpdate skipped", {
					reason: "frame already pending",
					eventName,
				});
				return;
			}
			const queued = scheduleUpdate();
			console.log("[HeaderContrast]", "scheduleUpdate triggered", {
				eventName,
				queued,
			});
		};

		addListener(window, "scroll", () => scheduleUpdateWithLog("scroll"), {
			passive: true,
		});
		addListener(window, "resize", () => scheduleUpdateWithLog("resize"));
		addListener(window, "orientationchange", () =>
			scheduleUpdateWithLog("orientationchange")
		);
		addListener(document, "shopify:section:load", () =>
			scheduleUpdateWithLog("shopify:section:load")
		);
		addListener(document, "shopify:block:select", () =>
			scheduleUpdateWithLog("shopify:block:select")
		);
		addListener(document, "shopify:block:deselect", () =>
			scheduleUpdateWithLog("shopify:block:deselect")
		);

		const resizeObserver = new ResizeObserver(() =>
			scheduleUpdateWithLog("ResizeObserver")
		);
		resizeObserver.observe(initialHeader);
		cleanupFns.push(() => resizeObserver.disconnect());

		scheduleUpdateWithLog("init");

		console.log("[HeaderContrast]", "Controller created");

		return {
			setHeader(target) {
				if (!target || !target.isConnected) return;
				if (state.header === target) {
					scheduleUpdateWithLog("setHeader:same");
					return;
				}

				if (state.header) {
					try {
						resizeObserver.unobserve(state.header);
					} catch (_error) {
						/* noop */
					}
				}

				state.header = target;
				state.currentTone = "";
				resizeObserver.observe(state.header);
				scheduleUpdateWithLog("setHeader:new");
			},
			update: () => scheduleUpdateWithLog("manual"),
			destroy() {
				while (cleanupFns.length) {
					const fn = cleanupFns.pop();
					try {
						fn();
					} catch (_error) {
						/* noop */
					}
				}
				resetHeaderContrast(state.header);
				console.log("[HeaderContrast]", "Controller destroyed");
			},
		};
	};

	const ensureDynamicHeaderContrast = () => {
		const currentHeader = document.querySelector(".shopify-section-header");
		const landingContext = getLandingPageContext();

		console.log("[HeaderContrast]", "ensureDynamicHeaderContrast", {
			headerFound: Boolean(currentHeader),
			landing: landingContext,
		});

		if (!currentHeader) return;

		if (!landingContext.isLanding) {
			if (dynamicContrastController) {
				dynamicContrastController.destroy();
				dynamicContrastController = null;
			}

			resetHeaderContrast(currentHeader);
			console.log("[HeaderContrast]", "Skipping: not on landing page", {
				landing: landingContext,
			});
			return;
		}

		if (!dynamicContrastController) {
			dynamicContrastController = createDynamicHeaderContrast(currentHeader);
		} else {
			dynamicContrastController.setHeader(currentHeader);
		}
	};

	const header = () => {
		const header = document.querySelector(".shopify-section-header");
		const headerAlways = document.querySelector(".header-always");

		console.log("[HeaderContrast]", "header() init", {
			headerFound: Boolean(header),
			landing: getLandingPageContext(),
		});

		if (headerAlways) {
			header.classList.add("always-show");
		}

		const menu = document.querySelector(".list-menu--inline");
		const menuLinks = document.querySelectorAll(".list-menu-item");
		const search = document.querySelector(".header__search");

		header.addEventListener("keydown", (e) => {
			if (e.code === "Escape" && search.isOpen) {
				search.close();
			}
		});

		let vh = window.innerHeight * 0.01;
		document.documentElement.style.setProperty("--vh", `${vh}px`);

		const annBar = document.querySelector(".section-announcement");
		const annBarObserver = new IntersectionObserver((entries) => {
			const [entry] = entries;

			if (entry.isIntersecting) {
				document.documentElement.style.setProperty(
					"--ann-height",
					`${entry.intersectionRect.height}px`
				);
			} else {
				document.documentElement.style.setProperty("--ann-height", `0px`);
			}
		});
		if (annBar) annBarObserver.observe(annBar);

		//menuLinks &&
		//	menuLinks.forEach((link) => {
		//		link.addEventListener("mouseenter", (e) => {
		//			if (link.classList.contains("list-menu--megamenu")) {
		//				link.classList.add("list-menu--megamenu-visible");
		//				if (
		//					header.classList.contains("color-background-2") &&
		//					!header.classList.contains("shopify-section-header-sticky")
		//				) {
		//					header.classList.remove("color-background-2");
		//				}

		//				menuLinks.forEach((el) => {
		//					if (el !== link) {
		//						el.classList.remove("list-menu--megamenu-visible");
		//					}
		//				});
		//			} else {
		//				menuLinks.forEach((el) => {
		//					el.classList.remove("list-menu--megamenu-visible");
		//				});
		//			}
		//		});
		//	});
		//menu &&
		//	menu.addEventListener("mouseleave", (e) => {
		//		menuLinks.forEach((link) => {
		//			link.classList.remove("list-menu--megamenu-visible");
		//		});
		//	});

		const addBackground = () => {
			if (document.querySelector(".overflow-hidden-drawer")) {
				return;
			}

			if (document.querySelector(".always-show")) {
				return;
			}

			const headerObserver = new ResizeObserver((entries) => {
				const [entry] = entries;
				if (entry.contentRect.width >= 1200) {
					if (
						header.classList.contains("color-background-2") &&
						!header.classList.contains("shopify-section-header-sticky")
					) {
						header.classList.remove("color-background-2");
					}

					if (
						header.classList.contains("section--have-overlay") &&
						!header.classList.contains("shopify-section-header-sticky")
					) {
						header.classList.add("have-background");
					}
				}
			});

			headerObserver.observe(header);
		};

		const removeBackground = () => {
			if (
				document.querySelector(".overflow-hidden") ||
				document.querySelector(".overflow-hidden-drawer")
			) {
				return;
			}

			if (document.querySelector(".always-show")) {
				return;
			}

			const headerObserver = new ResizeObserver((entries) => {
				const [entry] = entries;
				if (entry.contentRect.width >= 1200) {
					if (
						header.classList.contains("color-background-1") &&
						!header.classList.contains("shopify-section-header-sticky")
					) {
						header.classList.add("color-background-2");
					}

					if (
						header.classList.contains("section--have-overlay") &&
						!header.classList.contains("shopify-section-header-sticky")
					) {
						header.classList.remove("have-background");
					}
				}
			});

			headerObserver.observe(header);
		};

		header.addEventListener("mouseenter", addBackground);

		header.addEventListener("focus", addBackground);

		header.addEventListener("focusin", addBackground);

		header.addEventListener("mouseleave", removeBackground);

		header.addEventListener("focusout", removeBackground);

		document.addEventListener("DOMContentLoaded", () => {
			const firstSection = document.querySelector("main .shopify-section:first-child:not(.not-margin) .section--has-overlay:not(.article-template__header)");
			let isSectionVisible = false;
			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting && !isSectionVisible) {
						isSectionVisible = true;
						header.classList.add("entering-section", "color-background-2");
					} else if (!entry.isIntersecting && isSectionVisible) {
						isSectionVisible = false;
						header.classList.remove("entering-section");
					}
				},
				{ threshold: 0.15 }
			);

			if (firstSection) {
				observer.observe(firstSection);
			}

			window.addEventListener("scroll", () => {
				if (isSectionVisible) {
					header.classList.add("entering-section", "color-background-2");
				}
			});
		});

		//const megaMenuListLinks = () => {
		//	const megaMenuLinkWithSubmenu = document.querySelector(
		//		".mega-menu__list-item--children"
		//	);
		//	const megaMenuLinks = document.querySelectorAll(".mega-menu__list-item");
		//	if (megaMenuLinkWithSubmenu) {
		//		megaMenuLinks.forEach((link) => {
		//			link.classList.add("mega-menu__list-item--opacity");
		//		});
		//		megaMenuLinkWithSubmenu.classList.remove(
		//			"mega-menu__list-item--opacity"
		//		);
		//		megaMenuLinkWithSubmenu.classList.add("mega-menu__list-item--visible");
		//	}
		//	$(".mega-menu__list-item").mouseenter(function (event) {
		//		if (megaMenuLinkWithSubmenu) {
		//			megaMenuLinkWithSubmenu.classList.remove(
		//				"mega-menu__list-item--visible"
		//			);
		//		}
		//		$(this).removeClass("mega-menu__list-item--opacity");
		//		$(this).siblings().addClass("mega-menu__list-item--opacity");
		//		$(this).mouseleave(() => {
		//			$(this).siblings().removeClass("mega-menu__list-item--opacity");
		//		});
		//	});
		//	$(".mega-menu__list-item").focus(function (event) {
		//		if (megaMenuLinkWithSubmenu) {
		//			megaMenuLinkWithSubmenu.classList.remove(
		//				"mega-menu__list-item--visible"
		//			);
		//		}
		//		$(this).removeClass("mega-menu__list-item--opacity");
		//		$(this).siblings().addClass("mega-menu__list-item--opacity");
		//		$(this).mouseleave(() => {
		//			$(this).siblings().removeClass("mega-menu__list-item--opacity");
		//		});
		//	});
		//};

		//const megaMenuSublinks = () => {
		//	$(".mega-menu--list .mega-menu__submenu-item").mouseenter(function (
		//		event
		//	) {
		//		$(this).removeClass("mega-menu__submenu-item--opacity");
		//		$(this)
		//			.parent()
		//			.siblings()
		//			.find(".mega-menu__submenu-item")
		//			.addClass("mega-menu__submenu-item--opacity");
		//		$(this).mouseleave(() => {
		//			$(this)
		//				.parent()
		//				.siblings()
		//				.find(".mega-menu__submenu-item")
		//				.removeClass("mega-menu__submenu-item--opacity");
		//		});
		//	});
		//	$(".mega-menu--list .mega-menu__submenu-item").focusin(function (event) {
		//		$(this).removeClass("mega-menu__submenu-item--opacity");
		//		$(this)
		//			.parent()
		//			.siblings()
		//			.find(".mega-menu__submenu-item")
		//			.addClass("mega-menu__submenu-item--opacity");
		//		$(this).focusout(() => {
		//			$(this)
		//				.parent()
		//				.siblings()
		//				.find(".mega-menu__submenu-item")
		//				.removeClass("mega-menu__submenu-item--opacity");
		//		});
		//	});
		//};
		//megaMenuListLinks();
		//megaMenuSublinks();

		const main = document.querySelector("main");
		const breadcrumbs = document.querySelector(".breadcrumbs-wrapper");
		if (
			main
				.querySelectorAll(".shopify-section")[0]
				?.classList.contains("section--has-overlay") &&
			!main
				.querySelectorAll(".shopify-section")[0]
				?.classList.contains("not-margin")
		) {
			header.classList.add("color-background-1", "color-background-2");
			if (breadcrumbs) {
				breadcrumbs.classList.add("color-background-2");
			}
		}

		ensureDynamicHeaderContrast();
	};

	header();

	document.addEventListener("shopify:section:load", header);
	document.addEventListener("shopify:section:unload", header);
	document.addEventListener("shopify:section:reorder", header);
	window.addEventListener("load", ensureDynamicHeaderContrast);
})();
